import { Box, Chip, InputAdornment, Skeleton, Stack, TextField, useTheme } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import useGetCollection from "../hooks/getCollection";
import Genre from "../models/genre";
import Mood from "../models/mood";
import Tag from "../models/tag";
import ScreenBanner from "../components/screenBanner";
import latestTagImage from "../assets/LoFi.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useQuickFilterPortal from "../hooks/useQuickFilterPortal";
import { useNavigate } from "react-router-dom";
import ScreenBase from "./ScreenBase";

const TagScreen: FC = () => {
	const theme = useTheme();
	const quickFilter = useQuickFilterPortal();
	const navigate = useNavigate();

	const { data: genreData, loading: genreLoading } = useGetCollection<Genre[]>('/genre/all');
	const { data: moodData, loading: moodLoading } = useGetCollection<Mood[]>('/mood/all');

	const [tags, setTags] = useState<Tag[]>([]);
	const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
	const [filter, setFilter] = useState<string>('');

	const filterTags = useCallback((filter: string) => {
		if (tags && tags.length) {
			setFilteredTags(tags.filter((tag) => tag.tag.includes(filter.toLowerCase())));
		} else {
			setFilteredTags([]);
		}
	}, [tags]);

	useEffect(() => {
		let allTags = [
			...(genreData?.filter(g => !!g.genre).map(g => ({ id: g.id, tag: g.genre.toLowerCase() })) || []),
			...(moodData?.filter(m => !!m.mood).map(m => ({ id: m.id, tag: m.mood.toLowerCase() })) || [])
		];

		// Remove duplicates from the allTags array
		allTags = allTags.filter((tag, index, self) => self.findIndex(t => t.tag === tag.tag) === index);

		// Order them alphabetically
		allTags.sort((a, b) => a.tag.localeCompare(b.tag));

		setTags(allTags);

		setFilter('');
	}, [genreData, moodData]);

	useEffect(() => {
		filterTags(filter);
	}, [filter, filterTags]);

	const handleTagClick = (tag: string) => () => {
		quickFilter.setFilterText(tag);
		navigate({ pathname: '/tunes', search: `?q=${tag}` });
	};

	return (
		<ScreenBase
			fixedChildren={(
				<Stack spacing={2}>
					<ScreenBanner
						title="Tags"
						bannerImage={latestTagImage}
						height={120}
						verticalAdjust="42%"
						brightnessAdjust={0.75}
					/>
					<Stack direction="row" spacing={2} alignItems="center">
						<TextField
							variant="outlined"
							size="small"
							sx={{ width: { xs: '100%', sm: '100%', md: '100%', lg: '80%', xl: 600 } }}
							placeholder="Find tag by name..."
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start">
											<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} style={{ color: theme.palette.text.secondary }} />
										</InputAdornment>
									)
								}
							}}
							value={filter}
							onChange={(event) => setFilter(event.target.value)}
						/>
					</Stack>
				</Stack>
			)}
			scrollableChildren={(
				<Stack
					spacing={1}
					sx={{
						display: 'block',
						columnGap: '20px',
						columnFill: 'balance',
						columnWidth: 120
					}}
				>
					{!genreLoading && !moodLoading && (
						<>
							{filteredTags?.map((tag, index) => (
								<Box key={`tag-${index}`}>
									<Chip
										size="small"
										variant="outlined"
										label={tag.tag}
										sx={{ textTransform: 'capitalize' }}
										onClick={handleTagClick(tag.tag)}
									/>
								</Box>
							))}
						</>
					)}
					{(genreLoading || moodLoading) && (
						<>
							{[...Array(500)].map((x, index) => {
								// calculate a random width between 50 and 120 pixels
								const width = Math.floor(Math.random() * 90) + 30;

								return (
									<Skeleton
										key={`skel-${index}`}
										variant="text"
										sx={{
											width: width,
											height: 24,
											borderRadius: 12,
											transform: 'none'
										}}
									/>
								);
							})}
						</>
					)}
				</Stack>
			)}
		/>
	);
};

export default TagScreen;
